import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import config from "../../config";
import { formatApiError } from "../../utilities/apiErrorMessages";

const resolveList = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data?.data)) {
    return payload.data.data;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

export const GetBranchs = createAsyncThunk(
  "branchs/getAll",
  async (token, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
      const response = await fetch(`${config.apiUrl}/branchs`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue({
        message: error?.message ?? "Unable to fetch branches",
      });
    }
  }
);

export const AddBranch = createAsyncThunk(
  "branchs/create",
  async ({ token, values }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
      const response = await fetch(`${config.apiUrl}/branchs`, {
        method: "POST",
        body: JSON.stringify(values),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue({
        message: error?.message ?? "Unable to create branch",
      });
    }
  }
);

export const UpdateBranch = createAsyncThunk(
  "branchs/update",
  async ({ token, id, values }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
      const response = await fetch(`${config.apiUrl}/branchs/${id}`, {
        method: "PUT",
        body: JSON.stringify(values),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue({
        message: error?.message ?? "Unable to update branch",
      });
    }
  }
);

export const DeleteBranch = createAsyncThunk(
  "branchs/delete",
  async ({ token, id }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
      const response = await fetch(`${config.apiUrl}/branchs/${id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.status === 204 ? {} : await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return fulfillWithValue({
        id,
        message: data?.message ?? null,
      });
    } catch (error) {
      return rejectWithValue({
        message: error?.message ?? "Unable to delete branch",
      });
    }
  }
);

const initialState = {
  branches: [],
  loading: false,
  error: null,
  successMessage: null,
  selectedBranch: null,
};

const BranchsSlice = createSlice({
  name: "branchs",
  initialState,
  reducers: {
    selectBranch: (state, action) => {
      state.selectedBranch = action.payload;
    },
    clearBranchFeedback: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(GetBranchs.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(GetBranchs.fulfilled, (state, action) => {
        state.loading = false;
        state.branches = resolveList(action.payload);
        state.error = null;
        state.successMessage = null;
      })
      .addCase(GetBranchs.rejected, (state, action) => {
        state.loading = false;
        state.branches = [];
        state.error = formatApiError(action.payload, "Unable to fetch branches");
        state.successMessage = null;
      })
      .addCase(AddBranch.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(AddBranch.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message ?? null;
        state.error = null;
        const created = action.payload?.data;
        if (created) {
          state.branches = [...state.branches, created];
        }
      })
      .addCase(AddBranch.rejected, (state, action) => {
        state.loading = false;
        state.error = formatApiError(action.payload, "Unable to create branch");
        state.successMessage = null;
      })
      .addCase(UpdateBranch.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(UpdateBranch.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message ?? null;
        state.error = null;
        const updated = action.payload?.data;
        if (updated) {
          const index = state.branches.findIndex((branch) => branch.id === updated.id);
          if (index !== -1) {
            state.branches[index] = {
              ...state.branches[index],
              ...updated,
            };
          }
        }
      })
      .addCase(UpdateBranch.rejected, (state, action) => {
        state.loading = false;
        state.error = formatApiError(action.payload, "Unable to update branch");
        state.successMessage = null;
      })
      .addCase(DeleteBranch.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(DeleteBranch.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message ?? null;
        state.error = null;
        const id = action.payload?.id;
        if (id) {
          state.branches = state.branches.filter((branch) => branch.id !== id);
        }
      })
      .addCase(DeleteBranch.rejected, (state, action) => {
        state.loading = false;
        state.error = formatApiError(action.payload, "Unable to delete branch");
        state.successMessage = null;
      });
  },
});

export default BranchsSlice.reducer;
export const { selectBranch, clearBranchFeedback } = BranchsSlice.actions;
